import { Product } from '@df/catalog/product/product';
import { Checkout } from '@df/checkout/checkout.interfaces';
import { Nexus } from '@tomandco/nexus-api-types';

declare module '@df/catalog/product/product' {
    interface IProductDataV2 {
        first_simple_sku?: string;
        category_path?: string;
        googleId?: string;
    }
}

declare global {
    interface Window {
        dataLayer: any[];
        google_tag_manager: any[];
    }
}

export namespace Gtm {
    ///////////////////////////////////////////////////////////////////
    ///
    ///
    /// Google Analytics 4
    ///
    ///
    ///////////////////////////////////////////////////////////////////

    /**
     * Ecommerce item data
     *
     * @info Ready for GA4
     */
    export interface IEcommerceItem {
        // A product affiliation to designate a supplying company or brick and mortar store location.
        affiliation?: string; // "online store" by default
        // If coupon is used against the product ( will always be empty as vouchers are applied to orders)
        coupon?: string;
        // Currency 3 letter code
        currency: string;
        // The monetary discount value associated with the item
        discount?: number;
        // The index/position of the item in a list.
        index?: number;
        // The brand of the item.
        item_brand?: string;
        // The category of the item. If used as part of a category hierarchy or taxonomy
        // then this will be the first category.
        item_category?: string;
        // The second category hierarchy or additional taxonomy for the item.
        item_category2?: string;
        // The third category hierarchy or additional taxonomy for the item.
        item_category3?: string;
        // The fourth category hierarchy or additional taxonomy for the item.
        item_category4?: string;
        // The fifth category hierarchy or additional taxonomy for the item.
        item_category5?: string;
        // The ID of the item.
        item_id: string | number;
        // The ID of the list in which the item was presented to the user. Example value `related-products`
        item_list_id?: string;
        // The name of the list in which the item was presented to the user. Example value `Related Products`
        item_list_name?: string;
        // The name of the item.
        item_name: string;
        // The item variant or unique code or description for additional item details/options.
        item_variant?: string;
        // The physical location associated with the item (e.g. the physical store location).
        // It's recommended to use the Google Place ID that corresponds to the associated item.
        // A custom location ID can also be used.
        location_id?: string;
        // The monetary price of the item, in units of the specified currency parameter.
        price?: number;
        // Item quantity. If not set, quantity is set to 1.
        quantity?: number;
        // sale price
        sale?: boolean;
        // product_id
        product_id?: number;
    }

    /**
     * Available events.
     *
     * @info Ready for GA4
     */
    export type EEvent =
        // GA 4 events
        | 'addPaymentInfo'
        | 'addShipmentInfo'
        | 'beginCheckout'
        | 'selectItem'
        | 'viewCart'
        | 'viewItem'
        | 'viewItemList'
        | 'eventGA4'
        // other events
        | 'newsletter'
        | 'basketChange'
        | 'checkout'
        | 'checkoutOption'
        | 'checkoutSuccess'
        | 'contentView'
        | 'customerEmailProvided'
        | 'customerUpdate'
        | 'enhancedEvent'
        | 'interaction'
        | 'nonInteraction'
        | 'productClick'
        | 'productOptionSelected'
        | 'promotionClick'
        | 'promotionImpression'
        | 'tierCookieConsentChange';

    /**
     * GA4 e-commerce event
     *
     * @info Ready for GA4
     */
    export interface IEventEcommerce<E = any> {
        event: string;
        ecommerce: E;
    }

    /**
     * Universal event data type for GA4
     *
     * @info Ready for GA4
     */
    export interface IEventDataGA4 {
        [key: string]: any;
    }

    /**
     * Universal event type for GA4
     *
     * @info Ready for GA4
     */
    export interface IEventGA4 extends IEvent {
        ga4_event_name: string;
        ga4_event_data: IEventDataGA4;
    }

    export interface IEventCustom<T = any> {
        event: 'custom_event';
        event_name: string;
        event_data: T;
    }

    /**
     * Universal type for track GA4
     *
     * @info Ready for GA4
     */
    export interface IGA4TrackData {
        name: string;
        details: { [key: string]: any };
    }

    /**
     * `add_payment_info` event data
     *
     * @link https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtag#add_payment_info
     * @info Ready for GA4
     */
    export interface IEventAddPaymentInfoData {
        // Currency 3 letter code
        currency: string;
        // The monetary value of the event.
        value: number;
        // The coupon name/code associated with the event.
        coupon?: string;
        // The chosen method of payment.
        payment_type?: string;
        // The items for the event.
        items: IEcommerceItem[];
    }

    /**
     * `add_payment_info` event data
     *
     * @info Ready for GA4
     */
    export interface IEventAddPaymentInfo extends IEventEcommerce<IEventAddPaymentInfoData> {}

    /**
     * `add_shipment_info` event data
     *
     * @link https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtag#add_shipment_info
     * @info Ready for GA4
     */
    export interface IEventAddShipmentInfoData {
        // Currency 3 letter code
        currency: string;
        // The monetary value of the event.
        value: number;
        // Label for delivery - sent as "click and collect" for click and collect method
        shipping_tier?: string;
        // Magento code for shipping method
        shipping_code?: string;
        // The items for the event.
        items: IEcommerceItem[];
    }

    /**
     * `add_payment_info` event data
     *
     * @info Ready for GA4
     */
    export interface IEventAddShipmentInfo extends IEventEcommerce<IEventAddShipmentInfoData> {}

    /**
     * `begin_checkout` event data
     *
     * @link https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtag#begin_checkout
     * @info Ready for GA4
     */
    export interface IEventBeginCheckoutData {
        // Currency 3 letter code
        currency: string;
        // The monetary value of the event.
        value?: number;
        // The items for the event.
        items: IEcommerceItem[];
    }

    /**
     * `begin_checkout` event data
     *
     * @info Ready for GA4
     */
    export interface IEventBeginCheckout extends IEventEcommerce<IEventBeginCheckoutData> {}

    /**
     * `select_item` event data
     *
     * @link https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtag#select_item
     * @info Ready for GA4
     */
    export interface IEventSelectItemData {
        // The ID of the list in which the item was presented to the user. Example value `related-products`
        item_list_id?: string;
        // The name of the list in which the item was presented to the user. Example value `Related Products`
        item_list_name?: string;
        // The items for the event.
        items: IEcommerceItem[];
    }

    /**
     * `select_item` event
     *
     * @info Ready for GA4
     */
    export interface IEventSelectItem extends IEventEcommerce<IEventSelectItemData> {}

    export interface ITrackSelectItem<T = Product> {
        // The ID of the list in which the item was presented to the user. Example value `related-products`
        listId?: string;
        // The name of the list in which the item was presented to the user. Example value `Related Products`
        listName?: string;
        // The index/position of the item in a list.
        index: number;
        // The model of the item
        model: T;
    }

    /**
     * `view_item` event data
     *
     * @link https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtag#view_item
     * @info Ready for GA4
     */
    export interface IEventViewItemData {
        // Currency 3 letter code
        currency: string;
        // The monetary value of the event.
        value: number;
        // The items for the event.
        items: IEcommerceItem[];
    }

    /**
     * `view_item` event
     *
     * @info Ready for GA4
     */
    export interface IEventViewItem extends IEventEcommerce<IEventViewItemData> {}

    /**
     * `view_item_list` event data
     *
     * @link https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtag#view_item_list
     * @info Ready for GA4
     */
    export interface IEventViewItemListData {
        // The ID of the list in which the item was presented to the user. Example value `related-products`
        item_list_id?: string;
        // The name of the list in which the item was presented to the user. Example value `Related Products`
        item_list_name?: string;
        // The items for the event.
        items: IEcommerceItem[];
    }

    /**
     * `view_item_list` event
     *
     * @info Ready for GA4
     */
    export interface IEventViewItemList extends IEventEcommerce<IEventViewItemListData> {}

    export interface ITrackViewItemList<T = Product> {
        // The ID of the list in which the item was presented to the user. Example value `related-products`
        listId?: string;
        // The name of the list in which the item was presented to the user. Example value `Related Products`
        listName?: string;
        // The models of the items
        models: T[];
    }

    /**
     * `view_cart` event data
     *
     * @link https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtag#view_cart
     * @info Ready for GA4
     */
    export interface IEventViewCartData {
        // Currency 3 letter code
        currency: string;
        // The monetary value of the event.
        value: number;
        // The items for the event.
        items: IEcommerceItem[];
    }

    /**
     * `view_cart` event data
     *
     * @info Ready for GA4
     */
    export interface IEventViewCart extends IEventEcommerce<IEventViewCartData> {}

    /**
     * `select_item` event data
     *
     * @link https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtag#select_item
     * @info Ready for GA4
     */
    export interface IEventSelectItemData {
        // The ID of the list in which the item was presented to the user. Example value `related-products`
        item_list_id?: string;
        // The name of the list in which the item was presented to the user. Example value `Related Products`
        item_list_name?: string;
        // The items for the event.
        items: IEcommerceItem[];
    }

    /**
     * `newsletter` event data
     */
    export interface IEventNewsletterData {
        value: boolean;
        email: string;
        placement?: string;
    }

    /**
     * `newsletter` event
     */
    export interface IEventNewsletter extends IEventEcommerce<IEventNewsletterData> {}

    ///////////////////////////////////////////////////////////////////

    export enum ITrackMode {
        OnView = 'onview',
        OnLoad = 'onload'
    }

    export interface ITrackData {
        threshold?: number;
    }

    export interface ITrackImpressionData extends ITrackData {
        productId: number;
        position?: number;
        list?: string;
    }

    /**
     * Product click data
     */
    export interface ITrackProductClickData {
        productId: number;
        position?: number;
        list?: string;
    }

    export interface ITrackClickData {
        category?: string;
        action: string;
        value?: string;
        label?: string;
    }

    /**
     * Checkout step data
     */
    export interface ICheckoutStepData {
        step: number;
        option?: string;
    }

    /**
     * Product representation for GTM
     */
    export interface IProductImpressionData {
        // product name
        name: string;
        // product sku (or its parents)
        id: string | number;
        // product id as in magento
        product_id: number;
        // product price
        price: number;
        // branc/manufacturer name
        brand?: string;
        // category name
        category?: string;
        // qtu
        quantity?: number;
        // list (for impressions)
        list?: string;
        // position (for impressions)
        position?: number;
        // for configurable product we will pass first child sku here
        simple_sku?: string;
        // variant (properties of simple)
        variant?: string;
        image?: string;
        url?: string;
    }

    /**
     * Promotion representation for GTM
     */
    export interface IPromotionData {
        // promotion name
        name: string;
        // promotion ID
        id: string;
        // image url
        creative: string;
        // just a number
        position: number;
    }

    /**
     * Promotion representation for GTM
     */
    export interface IImpressionData {
        // product ID
        productId: number;
        // promotion name
        list: string;
        // promotion ID
        threshold?: number;
        // position on the list
        position: number;
    }

    ///////////////////////////////////////////////////////////////////
    ///
    ///
    /// GTM SERVICE EVENTS
    ///
    ///
    ///////////////////////////////////////////////////////////////////

    /**
     * Basic event
     */
    export interface IEvent {
        event: string;
        eventIsNonInteraction?: boolean;
    }

    /**
     * Regular GA event
     */
    export interface IEventRegular extends IEvent {
        eventAction?: string;
        eventCategory?: string;
        eventLabel?: string;
        eventValue?: string;
    }

    /**
     * Product click event data
     */
    export interface IEventProductClick extends IEventRegular {
        ecommerce: {
            click: {
                products: IProductImpressionData[];
                actionField?: {
                    list: string;
                    position: number;
                };
            };
        };
    }

    /**
     * Product click event data
     */
    export interface IEventPromotionClick extends IEventEcommerce {
        ecommerce: {
            promoClick: {
                promotions: IPromotionData[];
            };
        };
    }

    /**
     * Product click event data
     */
    export interface IEventPromotionImpression extends IEventEcommerce {
        ecommerce: {
            promoView: {
                promotions: IPromotionData[];
            };
        };
    }

    /**
     * Product click event data
     */
    export interface IEventImpressions extends IEventEcommerce {
        ecommerce: {
            currencyCode: string;
            impressions: IProductImpressionData[];
        };
    }

    export interface IEventSearch extends IEvent {
        event: 'search';
        search_term: string;
        search_type: 'preview' | 'redirect' | 'standard';
        number_of_results?: number;
    }

    /**
     * Transaction
     */
    export interface IEventPurchase extends IEventEcommerce {
        ecommerce: {
            currencyCode: string;
            purchase: {
                actionField: {
                    id: string;
                    affiliation?: string;
                    revenue: number;
                    tax: number;
                    shipping: number;
                    coupon?: string;
                };
                products: IProductImpressionData[];
            };
        };
    }

    ///////////////////////// CONTENT VIEW ////////////////////////////

    /**
     * Content view event
     */
    export interface IEventContentView extends IEvent {
        'content-name': string;
        'content-count': number;
        'content-data'?: IContentViewData;
        customer?: any;
        meta?: any;
        pageType: string;
    }

    /**
     * Content view event data
     */
    export interface IContentViewData {
        id: string | number;
        type: string;
        custom?: any;
        name?: string;
    }

    /**
     * Content view event data for product model
     */
    export interface IContentViewProduct extends IContentViewData {
        product_id: number;
    }

    /**
     * Content view event (when model is product)
     */
    export interface IEventContentViewProduct extends IEventContentView, IEventRegular {
        ecommerce: {
            currencyCode: string;
            detail: {
                products: IProductImpressionData[];
            };
        };
        'content-data': IContentViewProduct;
    }

    ///////////////////////// PROMOTION CLICK ///////////////////////////

    /**
     * Product click event data
     */
    export interface IEventPromotionClick extends IEventRegular {
        ecommerce: {
            promoClick: {
                promotions: IPromotionData[];
            };
        };
    }

    /**
     * Product click event data
     */
    export interface IEventPromotionImpression extends IEventRegular {
        ecommerce: {
            promoView: {
                promotions: IPromotionData[];
            };
        };
    }

    /**
     * Product click event data
     */
    export interface IEventImpressions extends IEventRegular {
        ecommerce: {
            currencyCode: string;
            impressions: IProductImpressionData[];
        };
    }

    /**
     * Product click event data
     */
    export interface IEventCustomerUpdate extends IEventRegular {
        customer: any;
    }

    /**
     * Custoemr emeila provided event data
     */
    export interface IEventCustomerEmail extends IEvent {
        customerEmail: string | undefined;
    }

    ///////////////////////// BASKET ///////////////////////////
    /**
     * Basket init event data
     */
    export interface IEventBasketInit extends IEvent {
        'basket-items': IProductImpressionData[];
        'basket-general': {
            currencyCode: string;
            value: number;
        };
    }

    ///////////////////////// CHECKOUT ///////////////////////////

    /**
     * Checkout event (to measure checkout steps)
     * https://developers.google.com/tag-manager/enhanced-ecommerce#checkoutstep
     */
    export interface IEventCheckout extends IEventRegular {
        ecommerce: {
            checkout: {
                actionField: ICheckoutStepData;
            };
            products: IProductImpressionData[];
        };
    }

    /**
     * Checkout option event (to measure checkout steps changes)
     * https://developers.google.com/tag-manager/enhanced-ecommerce#checkout_option
     */
    export interface IEventOptionCheckout extends IEventRegular {
        ecommerce: {
            checkout_option: {
                actionField: ICheckoutStepData;
            };
        };
    }

    export interface IEventBasket extends IEvent {
        'basket-items': IProductImpressionData[];
        'basket-general': {
            id?: number;
            totals?: Nexus.Lib.Basket.Totals;
            coupon?: string;
        };
    }

    export interface ICheckoutSubmitData {
        checkoutData: Checkout.IQuoteData;
    }
}
